import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, ListGroup, Badge, Row, Col } from 'react-bootstrap';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { stripHtml, isEmptyRichText } from '@components/common/RichText';

// ─── Question type metadata ───────────────────────────────────────────────────
const QUESTION_TYPES = [
  { value: 'multiple-choice', label: 'Multiple Choice', icon: '🔘', short: 'Single answer'    },
  { value: 'multi-select',    label: 'Multi-Select',    icon: '☑️',  short: 'Multiple answers' },
  { value: 'true-false',      label: 'True / False',    icon: '⊤⊥',  short: '2 choices'        },
  { value: 'fill-in',         label: 'Fill in Blank',   icon: '___', short: 'Type answer'      },
  { value: 'matching',        label: 'Matching',         icon: '↔',  short: 'Match pairs'      },
  { value: 'drag-drop',       label: 'Drag & Drop',     icon: '⇅',   short: 'Order items'      },
];

// ─── Quill configs ────────────────────────────────────────────────────────────
const questionModules = {
  toolbar: [
    [{ header: [1, 2, false] }],
    ['bold', 'italic', 'underline', 'code'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['code-block', 'blockquote'],
    ['link', 'image'],
    ['clean'],
  ],
};

const optionModules = {
  toolbar: [
    ['bold', 'italic', 'code'],
    ['link', 'image'],
    ['clean'],
  ],
};

const quillFormats = [
  'header', 'bold', 'italic', 'underline', 'code',
  'list', 'bullet', 'code-block', 'blockquote', 'link', 'image',
];

const explanationModules = {
  toolbar: [
    ['bold', 'italic', 'code'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['code-block', 'link'],
    ['clean'],
  ],
};

const explanationFormats = [
  'bold', 'italic', 'code', 'list', 'bullet', 'code-block', 'link',
];

// ─── Helpers ──────────────────────────────────────────────────────────────────
/** True if `option` matches `correctAnswer`, handling HTML vs plain text. */
function optionMatchesAnswer(option, correctAnswer) {
  if (!option || !correctAnswer) return false;
  return option === correctAnswer || stripHtml(option) === stripHtml(correctAnswer);
}

// ─── Component ────────────────────────────────────────────────────────────────
export function QuestionBlockEditor({ show, onHide, question, onSave }) {
  const [formData, setFormData] = useState({
    id: '',
    type: 'multiple-choice',
    text: '',
    options: ['', ''],
    correctAnswer: '',
    explanation: '',
    tags: [],
    leftItems: [],
    rightItems: [],
    items: [],
  });

  useEffect(() => {
    if (question) {
      const normalizedQuestion = {
        ...question,
        options: question.options && Array.isArray(question.options)
          ? question.options.map(opt => (typeof opt === 'string' ? opt : opt.text || ''))
          : question.options || ['', ''],
        correctAnswer: (() => {
          if (
            question.type === 'multi-select' &&
            Array.isArray(question.correctAnswer) &&
            Array.isArray(question.options)
          ) {
            return question.correctAnswer.map(answerId => {
              const option = question.options.find(opt => opt.id === answerId);
              return option ? option.text : answerId;
            });
          }
          return question.correctAnswer || '';
        })(),
        tags: Array.isArray(question.tags) ? question.tags : [],
      };
      setFormData(normalizedQuestion);
    } else {
      setFormData({
        id: `question-${Date.now()}`,
        type: 'multiple-choice',
        text: '',
        options: ['', ''],
        leftItems: [],
        rightItems: [],
        items: [],
        correctAnswer: '',
        explanation: '',
        tags: [],
      });
    }
  }, [question, show]);

  const set = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  // ── Option helpers ──────────────────────────────────────────────────────────
  const handleOptionChange = (index, value) => {
    setFormData(prev => {
      // Guard: if value is unchanged, skip update (prevents Quill init loop)
      if (prev.options[index] === value) return prev;

      const newOptions = [...prev.options];
      const oldOption = newOptions[index];
      newOptions[index] = value;

      // Keep correctAnswer in sync when the correct option is edited
      let newCorrectAnswer = prev.correctAnswer;
      if (prev.type === 'multi-select' && Array.isArray(prev.correctAnswer)) {
        newCorrectAnswer = prev.correctAnswer.map(a =>
          optionMatchesAnswer(oldOption, a) ? value : a
        );
      } else if (optionMatchesAnswer(oldOption, prev.correctAnswer)) {
        newCorrectAnswer = value;
      }

      return { ...prev, options: newOptions, correctAnswer: newCorrectAnswer };
    });
  };

  const addOption = () => set('options', [...formData.options, '']);

  const removeOption = (index) => {
    setFormData(prev => {
      const opt = prev.options[index];
      const newOptions = prev.options.filter((_, i) => i !== index);
      const wasCorrect = prev.type === 'multi-select'
        ? Array.isArray(prev.correctAnswer) && prev.correctAnswer.some(a => optionMatchesAnswer(opt, a))
        : optionMatchesAnswer(opt, prev.correctAnswer);
      return {
        ...prev,
        options: newOptions,
        correctAnswer: wasCorrect
          ? (prev.type === 'multi-select'
              ? (Array.isArray(prev.correctAnswer) ? prev.correctAnswer.filter(a => !optionMatchesAnswer(opt, a)) : [])
              : '')
          : prev.correctAnswer,
      };
    });
  };

  const isOptionCorrect = (option) => {
    if (!formData.correctAnswer || !option) return false;
    if (formData.type === 'multi-select') {
      const answers = Array.isArray(formData.correctAnswer) ? formData.correctAnswer : [];
      return answers.some(a => optionMatchesAnswer(option, a));
    }
    return optionMatchesAnswer(option, formData.correctAnswer);
  };

  const toggleCorrectOption = (option) => {
    if (formData.type === 'multi-select') {
      const current = Array.isArray(formData.correctAnswer) ? formData.correctAnswer : [];
      const already = current.some(a => optionMatchesAnswer(option, a));
      set('correctAnswer', already
        ? current.filter(a => !optionMatchesAnswer(option, a))
        : [...current, option]
      );
    } else {
      set('correctAnswer', option);
    }
  };

  // ── Validation & Save ───────────────────────────────────────────────────────
  const handleSubmit = (e) => {
    e.preventDefault();

    if (isEmptyRichText(formData.text)) {
      alert('Please enter the question text');
      return;
    }

    if (formData.type === 'multiple-choice' || formData.type === 'multi-select') {
      const validOptions = formData.options.filter(o => !isEmptyRichText(o));
      if (validOptions.length < 2) {
        alert('Please provide at least 2 answer options');
        return;
      }
      const hasAnswer = formData.type === 'multi-select'
        ? Array.isArray(formData.correctAnswer) && formData.correctAnswer.length > 0
        : Boolean(formData.correctAnswer);
      if (!hasAnswer) {
        alert('Please select the correct answer');
        return;
      }
    }

    if (formData.type === 'matching') {
      if (!formData.leftItems || formData.leftItems.length < 2) { alert('Please provide at least 2 left items'); return; }
      if (!formData.rightItems || formData.rightItems.length < 2) { alert('Please provide at least 2 right items'); return; }
      const unmatchedLeft = formData.leftItems.find(item => !(formData.correctAnswer || {})[item.id]);
      if (unmatchedLeft) { alert('Please match all left items to right items'); return; }
    }

    if (formData.type === 'drag-drop') {
      if (!formData.items || formData.items.length < 2) { alert('Please provide at least 2 items'); return; }
      if (formData.items.find(item => !item.text.trim())) { alert('Please fill in all item text'); return; }
    }

    const saveData = { ...formData };

    // Ensure correctAnswer uses the same string as stored in options (HTML)
    if (formData.type === 'multiple-choice') {
      const match = formData.options.find(o => optionMatchesAnswer(o, formData.correctAnswer));
      if (match) saveData.correctAnswer = match;
    }
    if (formData.type === 'multi-select' && Array.isArray(formData.correctAnswer)) {
      saveData.correctAnswer = formData.correctAnswer.map(ca => {
        const match = formData.options.find(o => optionMatchesAnswer(o, ca));
        return match || ca;
      });
    }
    if (formData.type === 'drag-drop') {
      saveData.correctAnswer = formData.items.map(item => item.id);
    }

    onSave(saveData);
  };

  const currentType = QUESTION_TYPES.find(t => t.value === formData.type) || QUESTION_TYPES[0];

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <Modal show={show} onHide={onHide} size="xl">
      <Modal.Header closeButton>
        <Modal.Title>
          {question ? '✏️ Edit Question' : '❓ New Question'}
          <Badge bg="secondary" className="ms-2" style={{ fontSize: '0.8rem' }}>
            {currentType.icon} {currentType.label}
          </Badge>
        </Modal.Title>
      </Modal.Header>

      <Form onSubmit={handleSubmit}>
        <Modal.Body>

          {/* ── Type Selector ── */}
          <Form.Group className="mb-4">
            <Form.Label className="fw-semibold">Question Type</Form.Label>
            <div className="d-flex flex-wrap gap-2">
              {QUESTION_TYPES.map(t => (
                <Button
                  key={t.value}
                  type="button"
                  variant={formData.type === t.value ? 'primary' : 'outline-secondary'}
                  onClick={() => set('type', t.value)}
                  className="d-flex flex-column align-items-center"
                  style={{ minWidth: 100, padding: '0.5rem 0.75rem', fontSize: '0.85rem' }}
                >
                  <span style={{ fontSize: '1.3rem', lineHeight: 1.2 }}>{t.icon}</span>
                  <span className="fw-semibold mt-1">{t.label}</span>
                  <span style={{ fontSize: '0.7rem', opacity: 0.75 }}>{t.short}</span>
                </Button>
              ))}
            </div>
          </Form.Group>

          {/* ── Question Text (rich) ── */}
          <Form.Group className="mb-4">
            <Form.Label className="fw-semibold">
              Question
              <span className="ms-2 text-muted small fw-normal">Supports rich text, code, and images</span>
            </Form.Label>
            <div className="border rounded" style={{ overflow: 'hidden' }}>
              <ReactQuill
                key={`q-text-${question?.id ?? 'new'}`}
                theme="snow"
                value={formData.text}
                onChange={val => set('text', val)}
                modules={questionModules}
                formats={quillFormats}
                placeholder="Enter your question here…"
                style={{ backgroundColor: 'white', minHeight: '130px' }}
              />
            </div>
          </Form.Group>

          {/* ── Multiple Choice / Multi-Select Options ── */}
          {(formData.type === 'multiple-choice' || formData.type === 'multi-select') && (
            <Form.Group className="mb-4">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <Form.Label className="fw-semibold mb-0">
                  Answer Options
                  <span className="ms-2 text-muted small fw-normal">
                    {formData.type === 'multi-select'
                      ? 'Check all correct answers'
                      : 'Select the one correct answer'}
                  </span>
                </Form.Label>
                <Button variant="outline-primary" size="sm" onClick={addOption}>+ Add Option</Button>
              </div>

              <div className="d-flex flex-column gap-2">
                {formData.options.map((option, index) => {
                  const correct = isOptionCorrect(option);
                  return (
                    <div
                      key={index}
                      className={`border rounded p-2 ${correct ? 'border-success' : ''}`}
                      style={{ background: correct ? 'rgba(25,135,84,0.06)' : 'white' }}
                    >
                      <div className="d-flex align-items-start gap-2">
                        {/* Correct-answer selector */}
                        <div className="d-flex flex-column align-items-center pt-2 gap-1 flex-shrink-0" style={{ width: 28 }}>
                          <Form.Check
                            type={formData.type === 'multi-select' ? 'checkbox' : 'radio'}
                            name="correctAnswer"
                            checked={correct}
                            onChange={() => toggleCorrectOption(option)}
                            title="Mark as correct answer"
                          />
                          {correct && (
                            <Badge bg="success" style={{ fontSize: '0.6rem' }}>✓</Badge>
                          )}
                        </div>

                        {/* Rich text editor for the option */}
                        <div className="flex-grow-1">
                          <ReactQuill
                            key={`opt-${question?.id ?? 'new'}-${index}`}
                            theme="snow"
                            value={option}
                            onChange={val => handleOptionChange(index, val)}
                            modules={optionModules}
                            formats={quillFormats}
                            placeholder={`Option ${index + 1}`}
                            style={{ backgroundColor: 'white', minHeight: '52px' }}
                          />
                        </div>

                        {formData.options.length > 2 && (
                          <Button
                            variant="outline-danger"
                            size="sm"
                            className="mt-1 flex-shrink-0"
                            onClick={() => removeOption(index)}
                          >✕</Button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </Form.Group>
          )}

          {/* ── True / False ── */}
          {formData.type === 'true-false' && (
            <Form.Group className="mb-4">
              <Form.Label className="fw-semibold">Correct Answer</Form.Label>
              <div className="d-flex gap-3">
                {['True', 'False'].map(opt => {
                  const selected = formData.correctAnswer === opt;
                  return (
                    <Button
                      key={opt}
                      type="button"
                      variant={selected ? (opt === 'True' ? 'success' : 'danger') : 'outline-secondary'}
                      className="flex-fill py-3"
                      style={{ fontSize: '1.1rem' }}
                      onClick={() => set('correctAnswer', opt)}
                    >
                      {opt === 'True' ? '✓ True' : '✗ False'}
                    </Button>
                  );
                })}
              </div>
            </Form.Group>
          )}

          {/* ── Fill in the Blank ── */}
          {formData.type === 'fill-in' && (
            <Form.Group className="mb-4">
              <Form.Label className="fw-semibold">Correct Answer</Form.Label>
              <Form.Control
                type="text"
                value={formData.correctAnswer}
                onChange={e => set('correctAnswer', e.target.value)}
                placeholder="Enter the exact correct answer"
              />
              <Form.Text className="text-muted">
                Use <code>___</code> in the question text to mark the blank. Comparison is case-insensitive.
              </Form.Text>
            </Form.Group>
          )}

          {/* ── Matching ── */}
          {formData.type === 'matching' && (
            <>
              <Row className="mb-3 g-3">
                <Col md={5}>
                  <Form.Label className="fw-semibold">Left Column</Form.Label>
                  <ListGroup className="mb-2">
                    {(formData.leftItems || []).map((item, i) => (
                      <ListGroup.Item key={i} className="d-flex align-items-center gap-2 py-2">
                        <span className="text-muted small flex-shrink-0">{i + 1}.</span>
                        <Form.Control
                          size="sm"
                          value={item.text || ''}
                          onChange={e => {
                            const updated = [...(formData.leftItems || [])];
                            updated[i] = { ...item, text: e.target.value };
                            set('leftItems', updated);
                          }}
                          placeholder={`Left item ${i + 1}`}
                        />
                        {formData.leftItems.length > 1 && (
                          <Button size="sm" variant="outline-danger" onClick={() =>
                            set('leftItems', formData.leftItems.filter((_, j) => j !== i))
                          }>✕</Button>
                        )}
                      </ListGroup.Item>
                    ))}
                  </ListGroup>
                  <Button size="sm" variant="outline-primary" onClick={() =>
                    set('leftItems', [...(formData.leftItems || []), { id: `left${Date.now()}`, text: '' }])
                  }>+ Add Left Item</Button>
                </Col>

                <Col md={2} className="d-flex align-items-center justify-content-center pt-4">
                  <span className="text-muted fs-4">↔</span>
                </Col>

                <Col md={5}>
                  <Form.Label className="fw-semibold">Right Column</Form.Label>
                  <ListGroup className="mb-2">
                    {(formData.rightItems || []).map((item, i) => (
                      <ListGroup.Item key={i} className="d-flex align-items-center gap-2 py-2">
                        <span className="text-muted small flex-shrink-0">{String.fromCharCode(65 + i)}.</span>
                        <Form.Control
                          size="sm"
                          value={item.text || ''}
                          onChange={e => {
                            const updated = [...(formData.rightItems || [])];
                            updated[i] = { ...item, text: e.target.value };
                            set('rightItems', updated);
                          }}
                          placeholder={`Right item ${i + 1}`}
                        />
                        {formData.rightItems.length > 1 && (
                          <Button size="sm" variant="outline-danger" onClick={() =>
                            set('rightItems', formData.rightItems.filter((_, j) => j !== i))
                          }>✕</Button>
                        )}
                      </ListGroup.Item>
                    ))}
                  </ListGroup>
                  <Button size="sm" variant="outline-primary" onClick={() =>
                    set('rightItems', [...(formData.rightItems || []), { id: `right${Date.now()}`, text: '' }])
                  }>+ Add Right Item</Button>
                </Col>
              </Row>

              {(formData.leftItems || []).length > 0 && (formData.rightItems || []).length > 0 && (
                <Form.Group className="mb-4">
                  <Form.Label className="fw-semibold">Correct Matches</Form.Label>
                  <div className="d-flex flex-column gap-2">
                    {(formData.leftItems || []).map((leftItem, i) => (
                      <div key={i} className="d-flex align-items-center gap-3">
                        <span className="text-muted" style={{ minWidth: 160 }}>
                          {leftItem.text || `Left item ${i + 1}`}
                        </span>
                        <span>→</span>
                        <Form.Select
                          size="sm"
                          style={{ maxWidth: 220 }}
                          value={(formData.correctAnswer || {})[leftItem.id] || ''}
                          onChange={e =>
                            set('correctAnswer', { ...(formData.correctAnswer || {}), [leftItem.id]: e.target.value })
                          }
                        >
                          <option value="">Select match…</option>
                          {(formData.rightItems || []).map(rightItem => (
                            <option key={rightItem.id} value={rightItem.id}>
                              {rightItem.text || rightItem.id}
                            </option>
                          ))}
                        </Form.Select>
                      </div>
                    ))}
                  </div>
                </Form.Group>
              )}
            </>
          )}

          {/* ── Drag & Drop ── */}
          {formData.type === 'drag-drop' && (
            <Form.Group className="mb-4">
              <Form.Label className="fw-semibold">
                Items in Correct Order
                <span className="ms-2 text-muted small fw-normal">Students will drag to reorder these</span>
              </Form.Label>
              <ListGroup className="mb-2">
                {(formData.items || []).map((item, index) => (
                  <ListGroup.Item key={index} className="d-flex align-items-center gap-2 py-2">
                    <Badge bg="secondary" className="flex-shrink-0">{index + 1}</Badge>
                    <Form.Control
                      value={item.text || ''}
                      onChange={e => {
                        const newItems = [...(formData.items || [])];
                        newItems[index] = { ...item, text: e.target.value };
                        set('items', newItems);
                      }}
                      placeholder={`Step ${index + 1}`}
                    />
                    <Button variant="outline-secondary" size="sm" disabled={index === 0} onClick={() => {
                      const ni = [...(formData.items || [])];
                      [ni[index - 1], ni[index]] = [ni[index], ni[index - 1]];
                      set('items', ni);
                    }}>↑</Button>
                    <Button variant="outline-secondary" size="sm" disabled={index === (formData.items || []).length - 1} onClick={() => {
                      const ni = [...(formData.items || [])];
                      [ni[index], ni[index + 1]] = [ni[index + 1], ni[index]];
                      set('items', ni);
                    }}>↓</Button>
                    {(formData.items || []).length > 2 && (
                      <Button variant="outline-danger" size="sm" onClick={() =>
                        set('items', formData.items.filter((_, i) => i !== index))
                      }>✕</Button>
                    )}
                  </ListGroup.Item>
                ))}
              </ListGroup>
              <Button variant="outline-primary" size="sm" onClick={() =>
                set('items', [...(formData.items || []), { id: `step${Date.now()}`, text: '' }])
              }>+ Add Item</Button>
            </Form.Group>
          )}

          {/* ── Explanation + Tags ── */}
          <Row className="g-3">
            <Col md={8}>
              <Form.Group>
                <Form.Label className="fw-semibold">
                  Explanation <span className="text-muted fw-normal small">(shown after answering)</span>
                </Form.Label>
                <div className="border rounded" style={{ overflow: 'hidden' }}>
                  <ReactQuill
                    key={`explanation-${question?.id ?? 'new'}`}
                    theme="snow"
                    value={formData.explanation}
                    onChange={val => set('explanation', val)}
                    modules={explanationModules}
                    formats={explanationFormats}
                    placeholder="Explain why this is the correct answer…"
                    style={{ backgroundColor: 'white', minHeight: '90px' }}
                  />
                </div>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label className="fw-semibold">Tags</Form.Label>
                <Form.Control
                  type="text"
                  value={Array.isArray(formData.tags) ? formData.tags.join(', ') : ''}
                  onChange={e => set('tags', e.target.value.split(',').map(t => t.trim()).filter(Boolean))}
                  placeholder="e.g., variables, loops"
                />
              </Form.Group>
            </Col>
          </Row>

        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={onHide}>Cancel</Button>
          <Button variant="primary" type="submit">Save Question</Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}
